import React, { useState, useEffect, useCallback } from 'react';
import { Trash2, Copy, Play, Film, Music, FileText, File, Loader2, RefreshCw, Search, ArrowLeft, Download, X, HardDrive, Plus, Subtitles, Clock, XCircle } from 'lucide-react';
import { getAuth } from 'firebase/auth';

const EXPORT_SERVICE_URL = import.meta.env.VITE_EXPORT_SERVICE_URL || 'https://export-service-400620783493.us-central1.run.app';

interface MediaFile {
    key: string;
    size: number;
    lastModified: string;
    url: string;
    name: string;
    category: 'upload' | 'subtitle' | 'export';
    type: 'video' | 'audio' | 'subtitle' | 'other';
    hasSubtitles?: boolean;
}

interface ActiveJob {
    id: string;
    status: string;
    currentStep?: string;
    progress?: number;
}

function formatBytes(bytes: number): string {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function timeAgo(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'maintenant';
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}j`;
}

const categoryColors: Record<string, { bg: string; text: string; border: string }> = {
    upload: { bg: 'rgba(59,130,246,0.15)', text: '#60a5fa', border: 'rgba(59,130,246,0.3)' },
    export: { bg: 'rgba(34,197,94,0.15)', text: '#4ade80', border: 'rgba(34,197,94,0.3)' },
    subtitle: { bg: 'rgba(168,85,247,0.15)', text: '#c084fc', border: 'rgba(168,85,247,0.3)' },
};

export const MediaPage: React.FC = () => {
    const [files, setFiles] = useState<MediaFile[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState<'all' | 'upload' | 'export' | 'subtitle'>('all');
    const [playingFile, setPlayingFile] = useState<MediaFile | null>(null);
    const [deleting, setDeleting] = useState<string | null>(null);
    const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
    const [creatingProject, setCreatingProject] = useState<string | null>(null);
    const [activeJobs, setActiveJobs] = useState<ActiveJob[]>([]);
    const [cancellingJob, setCancellingJob] = useState<string | null>(null);

    const fetchFiles = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const user = getAuth().currentUser;
            if (!user) { setError('Non connecté'); setLoading(false); return; }

            const res = await fetch(`${EXPORT_SERVICE_URL}/media/all/${user.uid}`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            const allFiles: MediaFile[] = data.files || [];

            // Check which videos have subtitles
            const subtitleKeys = new Set(allFiles.filter(f => f.category === 'subtitle').map(f => {
                // Extract clip/project identifiers from subtitle path
                const parts = f.key.split('/');
                return parts[parts.length - 1]?.replace('.json', '').replace('.srt', '');
            }));

            // Mark videos that have associated subtitles
            for (const f of allFiles) {
                if (f.type === 'video') {
                    // Check if any subtitle file contains this video's name
                    f.hasSubtitles = allFiles.some(sf =>
                        sf.category === 'subtitle' && sf.key.toLowerCase().includes(
                            f.name.replace(/\.\w+$/, '').toLowerCase().substring(0, 20)
                        )
                    );
                }
            }

            setFiles(allFiles);
        } catch (err) {
            setError(String(err));
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchActiveJobs = useCallback(async () => {
        // Use Firestore directly instead of REST to avoid 429s when server is busy
        try {
            const { collection, query, where, onSnapshot, getFirestore } = await import('firebase/firestore');
            const db = getFirestore();
            const activeStatuses = ['processing', 'merging', 'queued', 'analyzing', 'downloading', 'encoding', 'uploading', 'pending'];
            const jobsQuery = query(
                collection(db, 'export-jobs'),
                where('status', 'in', activeStatuses.slice(0, 10)) // Firestore 'in' limit = 10
            );
            onSnapshot(jobsQuery, (snapshot) => {
                const jobs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setActiveJobs(jobs);
            });
        } catch { /* silent */ }
    }, []);

    useEffect(() => { fetchFiles(); fetchActiveJobs(); }, [fetchFiles, fetchActiveJobs]);

    const handleDelete = async (file: MediaFile) => {
        if (!confirm(`Supprimer ${file.name} ?\n\n${formatBytes(file.size)} — ${file.category}`)) return;
        setDeleting(file.key);
        try {
            const res = await fetch(`${EXPORT_SERVICE_URL}/media/delete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ key: file.key }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            setFiles(prev => prev.filter(f => f.key !== file.key));
            if (playingFile?.key === file.key) setPlayingFile(null);
        } catch (err) {
            alert(`Erreur: ${err}`);
        } finally {
            setDeleting(null);
        }
    };

    const handleCopy = (url: string) => {
        navigator.clipboard.writeText(url);
        setCopiedUrl(url);
        setTimeout(() => setCopiedUrl(null), 2000);
    };

    const handleCreateProject = async (file: MediaFile) => {
        const user = getAuth().currentUser;
        if (!user) { alert('Non connecté'); return; }

        setCreatingProject(file.key);
        try {
            const { firestore } = await import('../firebase');
            const projectId = `id-${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
            const projectName = file.name.replace(/\.\w+$/, '').replace(/_/g, ' ');

            const projectData = {
                id: projectId,
                name: projectName,
                thumbnailUrl: '',
                clips: [{
                    id: `clip-${Date.now()}-${Math.random()}`,
                    name: file.name,
                    type: 'video',
                    url: file.url,
                    duration: 0,
                    startTime: 0,
                    endTime: 0,
                    volume: 1,
                    speed: 1,
                    audioSources: [{ id: 'original', type: 'original', url: file.url }],
                }],
                tracks: [{ id: 'track-0', clips: [] }],
                montageVersions: [],
                exports: [],
                createdAt: Date.now(),
                updatedAt: Date.now(),
            };

            await firestore.collection('users').doc(user.uid).collection('projects').doc(projectId).set(projectData);

            // Navigate to the project
            window.location.href = `/project/${projectId}`;
        } catch (err) {
            alert(`Erreur création projet: ${err}`);
        } finally {
            setCreatingProject(null);
        }
    };

    const handleCancelJob = async (jobId: string) => {
        setCancellingJob(jobId);
        try {
            const res = await fetch(`${EXPORT_SERVICE_URL}/cancel/${jobId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            if (res.ok) {
                setActiveJobs(prev => prev.filter(j => j.id !== jobId));
            }
        } catch (err) {
            alert(`Erreur: ${err}`);
        } finally {
            setCancellingJob(null);
        }
    };

    const handleForceCancel = async () => {
        if (!confirm('Annuler TOUS les jobs actifs ?')) return;
        try {
            await fetch(`${EXPORT_SERVICE_URL}/force-cancel-all`, { method: 'POST' });
            setActiveJobs([]);
        } catch (err) {
            alert(`Erreur: ${err}`);
        }
    };

    const filtered = files.filter(f => {
        if (filter !== 'all' && f.category !== filter) return false;
        if (search && !f.name.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
    });

    const totalSize = files.reduce((acc, f) => acc + (f.size || 0), 0);
    const videoCount = files.filter(f => f.type === 'video').length;
    const exportCount = files.filter(f => f.category === 'export').length;

    // Styles
    const s = {
        page: {
            minHeight: '100vh', height: '100vh', overflowY: 'auto' as const,
            background: 'linear-gradient(135deg, #0a0a0f 0%, #12121f 50%, #0a0a0f 100%)',
            color: '#e5e7eb', fontFamily: "'Inter', -apple-system, sans-serif",
        },
        header: {
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            background: 'rgba(10,10,15,0.9)', backdropFilter: 'blur(20px)',
            position: 'sticky' as const, top: 0, zIndex: 50,
        },
        card: {
            background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: 14, overflow: 'hidden' as const, transition: 'all 0.2s',
        },
        btn: (active: boolean) => ({
            padding: '7px 14px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 500,
            background: active ? 'rgba(129,140,248,0.2)' : 'rgba(255,255,255,0.04)',
            color: active ? '#818cf8' : '#9ca3af', transition: 'all 0.2s',
        }),
        actionBtn: {
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
            padding: '6px 0', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)',
            background: 'rgba(255,255,255,0.04)', color: '#9ca3af',
            cursor: 'pointer', fontSize: 11, transition: 'all 0.15s', flex: 1,
        } as React.CSSProperties,
    };

    return (
        <div style={s.page}>
            {/* Header */}
            <div style={s.header}>
                <div style={{ maxWidth: 1400, margin: '0 auto', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <a href="/" style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#6b7280', textDecoration: 'none', fontSize: 13 }}>
                            <ArrowLeft size={15} /> Retour
                        </a>
                        <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.08)' }} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <HardDrive size={20} style={{ color: '#818cf8' }} />
                            <h1 style={{ fontSize: 18, fontWeight: 700, margin: 0, background: 'linear-gradient(135deg, #818cf8, #a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                                Médias R2
                            </h1>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: 16, fontSize: 11, color: '#6b7280', alignItems: 'center' }}>
                        <span>{files.length} fichiers</span>
                        <span>{videoCount} vidéos</span>
                        <span>{exportCount} exports</span>
                        <span style={{ color: '#818cf8', fontWeight: 600 }}>{formatBytes(totalSize)}</span>
                        <button onClick={() => { fetchFiles(); fetchActiveJobs(); }} disabled={loading} style={{
                            display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 8,
                            border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)',
                            color: '#d1d5db', cursor: 'pointer', fontSize: 12,
                        }}>
                            <RefreshCw size={13} style={loading ? { animation: 'spin 1s linear infinite' } : undefined} />
                            Rafraîchir
                        </button>
                        <button onClick={async () => {
                            if (!confirm('ATTENTION: Vous êtes sur le point de TOUT supprimer dans votre espace R2 ET de supprimer tous vos projets LVMH. Êtes-vous sûr ?')) return;
                            const user = getAuth().currentUser;
                            if (!user) return alert('Non connecté');
                            setDeleting('all');

                            try {
                                // 1. Remove files from R2
                                const r2Res = await fetch(`${EXPORT_SERVICE_URL}/media/delete-all`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ userId: user.uid }),
                                });

                                if (!r2Res.ok) throw new Error(await r2Res.text());

                                // 2. Load all projects from Firestore to delete them
                                const { getProjectsFromFirestore, deleteProjectFromFirestore } = await import('../firestore');
                                const firestoreProjects = await getProjectsFromFirestore();

                                // 3. Delete all projects
                                let deletedProjectsCount = 0;
                                for (const p of firestoreProjects) {
                                    await deleteProjectFromFirestore(p.id);
                                    deletedProjectsCount++;
                                }

                                alert(`Toutes les vidéos ont été supprimées et ${deletedProjectsCount} projet(s) supprimé(s).`);
                                fetchFiles();
                            } catch (err) {
                                alert('Erreur: ' + err);
                            } finally {
                                setDeleting(null);
                            }
                        }} disabled={deleting === 'all' || loading || files.length === 0} style={{
                            display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 8,
                            border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.1)',
                            color: '#ef4444', cursor: 'pointer', fontSize: 12,
                        }}>
                            {deleting === 'all' ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Trash2 size={13} />}
                            Tout Supprimer
                        </button>
                    </div>
                </div>
            </div>

            {/* Active Jobs Panel */}
            {activeJobs.length > 0 && (
                <div style={{ maxWidth: 1400, margin: '0 auto', padding: '12px 24px 0' }}>
                    <div style={{
                        background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.2)',
                        borderRadius: 12, padding: '12px 16px',
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Clock size={14} style={{ color: '#eab308' }} />
                                <span style={{ fontSize: 13, fontWeight: 600, color: '#eab308' }}>
                                    {activeJobs.length} job{activeJobs.length > 1 ? 's' : ''} en cours
                                </span>
                            </div>
                            <button onClick={handleForceCancel} style={{
                                padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(239,68,68,0.3)',
                                background: 'rgba(239,68,68,0.1)', color: '#ef4444', cursor: 'pointer', fontSize: 11,
                            }}>
                                Tout annuler
                            </button>
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {activeJobs.map(job => (
                                <div key={job.id} style={{
                                    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                                    background: 'rgba(0,0,0,0.2)', borderRadius: 8, fontSize: 11,
                                }}>
                                    <Loader2 size={12} style={{ color: '#eab308', animation: 'spin 1s linear infinite' }} />
                                    <span style={{ color: '#fbbf24' }}>{job.id.slice(-8)}</span>
                                    <span style={{ color: '#9ca3af' }}>{job.currentStep || job.status}</span>
                                    {job.progress != null && <span style={{ color: '#6b7280' }}>{Math.round(job.progress)}%</span>}
                                    <button onClick={() => handleCancelJob(job.id)} disabled={cancellingJob === job.id} style={{
                                        background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 2,
                                    }}>
                                        <XCircle size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Toolbar */}
            <div style={{ maxWidth: 1400, margin: '0 auto', padding: '14px 24px', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: '1 1 250px' }}>
                    <Search size={15} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
                    <input
                        type="text" placeholder="Rechercher..."
                        value={search} onChange={e => setSearch(e.target.value)}
                        style={{
                            width: '100%', padding: '9px 12px 9px 34px', borderRadius: 10,
                            border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)',
                            color: '#e5e7eb', fontSize: 13, outline: 'none',
                        }}
                    />
                </div>
                {(['all', 'upload', 'export', 'subtitle'] as const).map(f => (
                    <button key={f} onClick={() => setFilter(f)} style={s.btn(filter === f)}>
                        {f === 'all' ? 'Tous' : f === 'upload' ? '📥 Uploads' : f === 'export' ? '📤 Exports' : '📝 ST'}
                        <span style={{ marginLeft: 5, opacity: 0.5 }}>
                            ({f === 'all' ? files.length : files.filter(x => x.category === f).length})
                        </span>
                    </button>
                ))}
            </div>

            {/* Content Grid */}
            <div style={{ maxWidth: 1400, margin: '0 auto', padding: '0 24px 40px' }}>
                {loading ? (
                    <div style={{ textAlign: 'center', paddingTop: 80 }}>
                        <Loader2 size={28} style={{ color: '#818cf8', animation: 'spin 1s linear infinite', margin: '0 auto' }} />
                        <p style={{ marginTop: 10, color: '#6b7280', fontSize: 13 }}>Chargement des médias...</p>
                    </div>
                ) : error ? (
                    <div style={{ textAlign: 'center', paddingTop: 80 }}>
                        <p style={{ color: '#ef4444', fontSize: 13 }}>Erreur: {error}</p>
                        <button onClick={fetchFiles} style={{ marginTop: 10, padding: '7px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.2)', color: '#ef4444', border: 'none', cursor: 'pointer', fontSize: 12 }}>
                            Réessayer
                        </button>
                    </div>
                ) : filtered.length === 0 ? (
                    <div style={{ textAlign: 'center', paddingTop: 80, color: '#6b7280' }}>
                        <HardDrive size={40} style={{ opacity: 0.2, margin: '0 auto 10px', display: 'block' }} />
                        <p style={{ fontSize: 13 }}>Aucun fichier trouvé</p>
                    </div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
                        {filtered.map(file => {
                            const isDeleting = deleting === file.key;
                            const isCreating = creatingProject === file.key;
                            const catStyle = categoryColors[file.category] || categoryColors.upload;

                            return (
                                <div key={file.key} style={{ ...s.card, opacity: isDeleting ? 0.3 : 1 }}
                                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(129,140,248,0.25)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; }}
                                >
                                    {/* Preview — NO preload, only plays on click */}
                                    {file.type === 'video' ? (
                                        <div style={{ height: 150, background: '#000', position: 'relative', cursor: 'pointer' }}
                                            onClick={() => setPlayingFile(playingFile?.key === file.key ? null : file)}>
                                            {playingFile?.key === file.key ? (
                                                <video src={file.url} style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                                    controls autoPlay />
                                            ) : (
                                                <div style={{
                                                    width: '100%', height: '100%', display: 'flex', alignItems: 'center',
                                                    justifyContent: 'center', background: 'linear-gradient(135deg, #111 0%, #1a1a2e 100%)',
                                                }}>
                                                    <div style={{
                                                        width: 44, height: 44, borderRadius: '50%', display: 'flex',
                                                        alignItems: 'center', justifyContent: 'center',
                                                        background: 'rgba(129,140,248,0.15)', border: '2px solid rgba(129,140,248,0.3)',
                                                    }}>
                                                        <Play size={20} style={{ color: '#818cf8', marginLeft: 2 }} />
                                                    </div>
                                                </div>
                                            )}
                                            {/* Badges overlaid */}
                                            <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 4 }}>
                                                {file.hasSubtitles && (
                                                    <span style={{
                                                        padding: '2px 6px', borderRadius: 6, fontSize: 10, fontWeight: 600,
                                                        background: 'rgba(168,85,247,0.3)', color: '#c084fc', backdropFilter: 'blur(4px)',
                                                        display: 'flex', alignItems: 'center', gap: 3,
                                                    }}>
                                                        <Subtitles size={10} /> ST
                                                    </span>
                                                )}
                                            </div>
                                            {/* Size badge */}
                                            <div style={{
                                                position: 'absolute', bottom: 8, left: 8, fontSize: 10, color: 'rgba(255,255,255,0.6)',
                                                background: 'rgba(0,0,0,0.5)', padding: '2px 6px', borderRadius: 4, backdropFilter: 'blur(4px)'
                                            }}>
                                                {formatBytes(file.size)}
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{
                                            height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            background: 'rgba(255,255,255,0.015)'
                                        }}>
                                            {file.type === 'audio' ? <Music size={22} style={{ color: '#6b7280' }} /> :
                                                file.type === 'subtitle' ? <FileText size={22} style={{ color: '#6b7280' }} /> :
                                                    <File size={22} style={{ color: '#6b7280' }} />}
                                        </div>
                                    )}

                                    {/* Info */}
                                    <div style={{ padding: '10px 12px' }}>
                                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 6 }}>
                                            <p style={{ fontSize: 12, fontWeight: 600, margin: 0, wordBreak: 'break-all', lineHeight: 1.3, flex: 1 }}>
                                                {file.name}
                                            </p>
                                            <span style={{
                                                fontSize: 9, padding: '2px 7px', borderRadius: 10,
                                                border: `1px solid ${catStyle.border}`, background: catStyle.bg,
                                                color: catStyle.text, whiteSpace: 'nowrap', fontWeight: 600,
                                            }}>
                                                {file.category}
                                            </span>
                                        </div>

                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 10, color: '#6b7280' }}>
                                            <span>{file.type !== 'video' ? formatBytes(file.size) : ''}</span>
                                            <span>{file.lastModified ? timeAgo(file.lastModified) : '—'}</span>
                                        </div>

                                        {/* Actions */}
                                        <div style={{ display: 'flex', gap: 5, marginTop: 8 }}>
                                            {/* Create Project (video only) */}
                                            {file.type === 'video' && (
                                                <button onClick={() => handleCreateProject(file)} disabled={isCreating}
                                                    style={{ ...s.actionBtn, background: isCreating ? 'rgba(129,140,248,0.15)' : 'rgba(129,140,248,0.08)', color: '#818cf8', borderColor: 'rgba(129,140,248,0.2)' }}>
                                                    {isCreating ? <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <Plus size={11} />}
                                                    Projet
                                                </button>
                                            )}

                                            {/* Copy URL */}
                                            <button onClick={() => handleCopy(file.url)}
                                                style={{ ...s.actionBtn, background: copiedUrl === file.url ? 'rgba(34,197,94,0.15)' : undefined, color: copiedUrl === file.url ? '#22c55e' : '#9ca3af' }}>
                                                <Copy size={11} />
                                                {copiedUrl === file.url ? '✓' : 'URL'}
                                            </button>

                                            {/* Download */}
                                            {(file.type === 'video' || file.type === 'audio') && (
                                                <a href={file.url} download style={{ ...s.actionBtn, textDecoration: 'none' }}>
                                                    <Download size={11} /> DL
                                                </a>
                                            )}

                                            {/* Delete */}
                                            <button onClick={() => handleDelete(file)} disabled={isDeleting}
                                                style={{ ...s.actionBtn, flex: 'none', padding: '6px 8px', borderColor: 'rgba(239,68,68,0.15)', background: 'rgba(239,68,68,0.08)', color: '#ef4444' }}>
                                                {isDeleting ? <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <Trash2 size={11} />}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Full-screen video modal */}
            {playingFile && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.9)',
                    backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
                }} onClick={() => setPlayingFile(null)}>
                    <div style={{ background: '#111', borderRadius: 14, width: '100%', maxWidth: 900, border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden' }}
                        onClick={e => e.stopPropagation()}>
                        {/* Modal header with title + actions */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#e5e7eb', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{playingFile.name}</p>
                                <p style={{ margin: '2px 0 0', fontSize: 11, color: '#6b7280' }}>{formatBytes(playingFile.size)} • {playingFile.category}</p>
                            </div>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginLeft: 12 }}>
                                {/* Create Project */}
                                {playingFile.type === 'video' && (
                                    <button onClick={() => handleCreateProject(playingFile)} disabled={creatingProject === playingFile.key}
                                        style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 8, border: '1px solid rgba(129,140,248,0.3)', background: 'rgba(129,140,248,0.1)', color: '#818cf8', cursor: 'pointer', fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap' }}>
                                        {creatingProject === playingFile.key ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Plus size={13} />}
                                        Créer Projet
                                    </button>
                                )}
                                {/* Copy URL */}
                                <button onClick={() => handleCopy(playingFile.url)}
                                    style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', background: copiedUrl === playingFile.url ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.05)', color: copiedUrl === playingFile.url ? '#22c55e' : '#9ca3af', cursor: 'pointer', fontSize: 12 }}>
                                    <Copy size={13} />
                                    {copiedUrl === playingFile.url ? '✓' : 'URL'}
                                </button>
                                {/* Download */}
                                <a href={playingFile.url} download
                                    style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', color: '#9ca3af', textDecoration: 'none', fontSize: 12 }}>
                                    <Download size={13} />
                                </a>
                                {/* Delete */}
                                <button onClick={() => { handleDelete(playingFile); }}
                                    style={{ display: 'flex', alignItems: 'center', padding: '6px 10px', borderRadius: 8, border: '1px solid rgba(239,68,68,0.2)', background: 'rgba(239,68,68,0.1)', color: '#ef4444', cursor: 'pointer', fontSize: 12 }}>
                                    <Trash2 size={13} />
                                </button>
                                {/* Close */}
                                <button onClick={() => setPlayingFile(null)} style={{ background: 'rgba(255,255,255,0.05)', border: 'none', borderRadius: 8, padding: 6, cursor: 'pointer', color: '#9ca3af' }}>
                                    <X size={16} />
                                </button>
                            </div>
                        </div>
                        <video src={playingFile.url} controls autoPlay style={{ width: '100%', maxHeight: '80vh', display: 'block' }} />
                    </div>
                </div>
            )}

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
};

export default MediaPage;
